import React, { useEffect, useState } from "react";
import { api, unwrap } from "@/api/client";
import { StatCard, Section, Empty } from "@/components/ui/kit";
import { fmtDate } from "@/utils/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { Users, HandCoins, Wallet, HeartHandshake, ArrowUpRight, Filter } from "lucide-react";
import { Link } from "react-router-dom";

const CHART_COLORS = ["#059669","#0D9488","#D97706","#4F46E5","#E11D48","#7C3AED","#0EA5E9"];

// Dynamic currency formatter
const fmtCurrency = (amount, currencyCode = "INR") => {
  if (amount == null || isNaN(amount)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  
  // New state to control which currency's analytics we are looking at
  const [selectedCurrency, setSelectedCurrency] = useState("INR");

  useEffect(() => {
    api.get("/admin/dashboard")
      .then(r => setD(unwrap(r)))
      .catch(e => setErr(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-muted-foreground">Loading dashboard…</div>;
  if (err) return <Empty title="Dashboard unavailable" note={err} testid="admin-dashboard-error" />;

  // Safely extract the data for the currently selected currency. 
  // If the currency doesn't exist in the data yet, fallback to zeros/empty arrays.
  const currencyData = d?.analytics?.[selectedCurrency] || {
    totalDonations: 0,
    totalAllocated: 0,
    totalUtilized: 0,
    donationCount: 0,
    donationsByProject: [],
    donationsByMonth: [],
    utilizationByProject: []
  };

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">DRMS Admin Portal</div>
          <h1 className="mt-1 font-display text-4xl font-extrabold text-emerald-950 tracking-tight">
            Admin Dashboard
          </h1>
        </div>
        
        {/* Currency Selector */}
        <div className="flex items-center gap-2 bg-white border border-emerald-900/10 p-2 rounded-lg shadow-sm">
          <Filter className="w-4 h-4 text-emerald-700 ml-1" />
          <span className="text-sm font-medium text-emerald-900">View analytics in:</span>
          <select 
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="text-sm bg-gray-50 border-gray-200 rounded-md focus:ring-emerald-500 focus:border-emerald-500 font-semibold"
          >
            {/* Populate options dynamically based on what the API returns */}
            {Object.keys(d?.analytics || { INR: {} }).map(currency => (
              <option key={currency} value={currency}>{currency}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-total-donors" label="Total Donors (Global)" value={d.totalDonors} sub={`${d.individualDonors} individual • ${d.corporateDonors} corporate`} />
        
        {/* These cards now update dynamically when the dropdown changes */}
        <StatCard testid="stat-total-donations" label={`Total Donations (${selectedCurrency})`} value={fmtCurrency(currencyData.totalDonations, selectedCurrency)} sub={`${currencyData.donationCount} transactions`} tone="accent" />
        <StatCard testid="stat-total-allocated" label={`Funds Allocated (${selectedCurrency})`} value={fmtCurrency(currencyData.totalAllocated, selectedCurrency)} sub="Across projects & activities" tone="warm" />
        <StatCard testid="stat-total-utilized" label={`Funds Utilized (${selectedCurrency})`} value={fmtCurrency(currencyData.totalUtilized, selectedCurrency)} sub={`${d.totalBeneficiaries} total beneficiaries`} tone="coral" />
      </div>

      <Section title="Donations flow" kicker={`Analytics for ${selectedCurrency}`}>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-emerald-900/10 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="overline">By Project / Activity</div>
              <HandCoins className="w-4 h-4 text-emerald-800" />
            </div>
            <div className="h-72 mt-3">
              {currencyData.donationsByProject?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={currencyData.donationsByProject}>
                    <CartesianGrid stroke="#EDE7DA" strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v)=>fmtCurrency(v, selectedCurrency)} />
                    <Bar dataKey="amount" fill="#059669" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty title={`No ${selectedCurrency} donations yet`} />}
            </div>
          </div>

          <div className="rounded-xl border border-emerald-900/10 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="overline">Last 12 months</div>
              <Wallet className="w-4 h-4 text-emerald-800" />
            </div>
            <div className="h-72 mt-3">
              {currencyData.donationsByMonth?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={currencyData.donationsByMonth}>
                    <CartesianGrid stroke="#EDE7DA" strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v)=>fmtCurrency(v, selectedCurrency)} />
                    <Line type="monotone" dataKey="amount" stroke="#D97706" strokeWidth={2.5} dot={{ r: 4, fill: "#D97706" }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Empty title={`No ${selectedCurrency} monthly data`} />}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Reach" kicker="Beneficiaries & utilization">
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="rounded-xl border border-emerald-900/10 bg-white p-6">
            {/* Beneficiaries are usually not currency-dependent, so this uses global data `d` */}
            <div className="overline">Beneficiaries by type</div>
            <div className="h-64 mt-3">
              {d.beneficiariesByType?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={d.beneficiariesByType} dataKey="count" nameKey="type" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {d.beneficiariesByType.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty title="No beneficiaries" />}
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-emerald-900/10 bg-white p-6">
            <div className="overline">Utilization by project ({selectedCurrency})</div>
            <div className="h-64 mt-3">
              {currencyData.utilizationByProject?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={currencyData.utilizationByProject} layout="vertical">
                    <CartesianGrid stroke="#EDE7DA" strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip formatter={(v)=>fmtCurrency(v, selectedCurrency)} />
                    <Bar dataKey="amount" fill="#0D9488" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty title={`No ${selectedCurrency} utilization yet`} />}
            </div>
          </div>
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-emerald-900/10 bg-white p-6" data-testid="recent-donations-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Recent (All Currencies)</div>
              <div className="font-display text-xl font-bold text-emerald-950">Latest donations</div>
            </div>
            <Link to="/admin/donations" className="text-xs font-semibold text-emerald-800 hover:underline inline-flex items-center gap-1">
              View all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="mt-3 divide-y divide-emerald-900/5">
            {d.recentDonations?.length ? d.recentDonations.map((r) => (
              <div key={r.donationId} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold text-emerald-950">{r.donorName || "—"}</div>
                  <div className="text-xs text-muted-foreground">{r.purpose} • {fmtDate(r.date)}</div>
                </div>
                {/* Dynamically format based on the specific row's currency */}
                <div className="mono font-semibold text-emerald-900">{fmtCurrency(r.amount, r.currencyCode)}</div>
              </div>
            )) : <Empty title="No donations yet" />}
          </div>
        </div>

        {/* ... (Recent Utilizations list remains the same structure as Recent Donations) ... */}
        
      </div>
    </div>
  );
}